import { AnyExtension, mergeAttributes } from "@tiptap/core";
// import CustomKeymap from "./custom-keymap";
// import { ImageResizer } from "./image-resizer";
// import { Twitter } from "./twitter";
// import { Mathematics } from "./mathematics";
// import UpdatedImage from "./updated-image";
import CharacterCount from "@tiptap/extension-character-count";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TiptapImage from "@tiptap/extension-image";
import TiptapLink from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import UniqueID from "@tiptap/extension-unique-id";
import Youtube from "@tiptap/extension-youtube";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from "@/tiptap/lib/tiptap-utils";
// --- Custom Extensions ---
import { Selection } from "@/tiptap/tiptap-extensions/common/selection-extension";
import { ImageUploadNode } from "@/tiptap/tiptap-node/image-upload-node/image-upload-node-extension";

import { nanoid } from "nanoid";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import Mention from "@tiptap/extension-mention";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { Document } from "@tiptap/extension-document";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";

import Emoji, { gitHubEmojis } from '@tiptap/extension-emoji'
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { Columns, Column } from "./MultiColumn";
import { isChangeOrigin } from "@tiptap/extension-collaboration";
import { SlashCommand } from "./SlashCommand";
import TrailingNode from "./trailing-node-extension";
import { InlineButtonNode } from "./inline-button/inline-button-node";
import { InlineImageNode } from "./inline-image/inline-image-node";
import { Kbd } from "./kbd-extension";
import { Small } from "./small-mark-extension";
import { VideoNode } from "./video-extension";

// Use 'common' languages (37) instead of 'all' (200+) to reduce bundle size
// Includes: JS, TS, Python, Java, Go, Rust, CSS, HTML, JSON, YAML, SQL, Bash, etc.
const lowlight = createLowlight(common);

const PlaceholderExtension = Placeholder.configure({
  includeChildren: true,
  showOnlyCurrent: false,
  // placeholder: ({ node }) => {
  //   // if (node.type.name === 'heading') {
  //   //   return `Heading ${node.attrs.level}`
  //   // }
  //   return "Write, or Press '/' for commands";
  // },
  placeholder: () => "Write, or Press '/' for commands",
});

const CodeBlockExtension = CodeBlockLowlight.configure({
  lowlight,
  defaultLanguage: "javascript",
});

const HighlightExtension = Highlight.configure({
  multicolor: true,
});

const MarkdownExtension = Markdown.configure({
  html: true,
  transformCopiedText: true,
});

const MentionExtension = Mention.configure({
  HTMLAttributes: {
    class: "mention",
  },
});

const DocumentExtension = Document.extend({
  content: "(block|columns)*", // Changed from + to * to allow empty content
});

// const BubbleMenuExtension = BubbleMenu.configure({
//   options: {
//     // FloatingUI options
//     strategy: 'absolute',
//     placement: 'top',
//     arrow: false,
//   },
// })

const HorizontalRuleExtension = HorizontalRule.extend({
  renderHTML() {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, { "data-type": this.name }),
      ["hr"],
    ];
  },
});

export const CommonExtensions: AnyExtension[] = [
  DocumentExtension,
  StarterKit.configure({
    document: false,
    dropcursor: false,
    // heading: false,
    horizontalRule: false,
    // blockquote: false,
    codeBlock: false,
  }),
  Emoji.configure({
    emojis: gitHubEmojis.filter(
      (emoji) => !emoji.name.includes("regional")
    ),
    forceFallbackImages: true,
  }),
  UniqueID.configure({
    types: [
      "heading",
      "paragraph",
      // "listItem", // cannot add unique id because of error: RangeError: Invalid content for node type listItem
      "blockquote",
      "codeBlock",
      "table",
      "Accordion",
      "AccordionGroup",
      "Admonition",
      "InternalNote",
      "Card",
      "CardGroup",
      "Steps",
      "Step",
      "Screenshot",
      "Tab",
      "Tabs"
    ],
    attributeName: "id",
    filterTransaction: (transaction) => !isChangeOrigin(transaction),
    generateID() {
      return nanoid(10);
    },
  }),
  TaskItem.configure({ nested: true }),
  TaskList,
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
  TiptapImage,
  MentionExtension,
  MarkdownExtension,
  SlashCommand,
  // BubbleMenuExtension,
  TextStyle,
  Color,
  CodeBlockExtension,
  HighlightExtension,
  HorizontalRuleExtension,
  Dropcursor.configure({
    width: 2,
    class: "ProseMirror-dropcursor border-black",
  }),
  // CustomKeymap,
  // UpdatedImage,
  Youtube.extend({
    renderHTML({ HTMLAttributes }) {
      // Filter out undefined/null attributes to prevent literal "undefined" in output
      const cleanedAttrs: Record<string, any> = {};

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          cleanedAttrs[key] = value;
        }
      });

      return ['div', { 'data-youtube-video': '' },
        ['iframe', cleanedAttrs]
      ];
    },
  }),
  // Twitter,
  // Mathematics,
  CharacterCount.configure({ limit: 50000 }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Underline,
  // ImageResizer,
  PlaceholderExtension,
  Typography,
  Superscript,
  Subscript,
  Selection,
  Columns,
  Column,
  ImageUploadNode.configure({
    accept: "image/*",
    maxSize: MAX_FILE_SIZE,
    limit: 3,
    upload: handleImageUpload,
    onError: (error) => console.error("Upload failed:", error),
  }),
  // TrailingNode,
  TiptapLink.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        'data-editing': {
          default: null,
          parseHTML: element => element.getAttribute('data-editing'),
          renderHTML: attributes => {
            if (!attributes['data-editing']) {
              return {}
            }
            return {
              'data-editing': attributes['data-editing']
            }
          }
        }
      }
    }
  }).configure({ openOnClick: false }),
  InlineButtonNode.configure({ openOnClick: false }),
  InlineImageNode,
  Kbd,
  Small,
  VideoNode,
];
